<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">
    <div class="row no-gutters h-100">
      <div class="col-2 p-2">
        <AnalyticsDashboardValueRow>
          <div class="col">
            <ReportFilterPopover
              headerTitle="Daily Order Value Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :appendToBody="true"
              alignment="left"
              placement="bottom-end"
              @apply="applyOrderValueFilter"
              @reset="resetOrderValueFilter">
              <template v-slot:popover>
                <ReportDateRangeSelect 
                  :startDate="editingOrderValueFilter.startDate"
                  :endDate="editingOrderValueFilter.endDate"
                  @updateStartDate="editingOrderValueFilter.startDate = $event"
                  @updateEndDate="editingOrderValueFilter.endDate = $event"
                />
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedOrderValueFilter.startDate | formatDate}} - {{appliedOrderValueFilter.endDate | formatDate}}
                </div>                
              </template>
            </ReportFilterPopover>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Total Order Value" highlight>
              {{formatCurrency(orderSummaryData.orderTotalValue)}}
              <template v-slot:info>Over {{orderSummaryData.orderTotalCount}} order{{displayCountPlural(orderSummaryData.orderTotalCount)}} during the period</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Average Yearly Value">
              {{formatCurrency(orderSummaryData.averageOrderValue)}}
              <template v-slot:info>Over {{orderSummaryData.orderTotalCount}} order{{displayCountPlural(orderSummaryData.orderTotalCount)}} during the period</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
        <AnalyticsDashboardValueRow v-if="orderSummaryData.largestOrderInfo">
          <div class="col">
            <AnalyticsDashboardValue title="Largest Order">
              {{formatCurrency(orderSummaryData.largestOrderInfo.totalPrice)}}
              <template v-slot:info>Order <LinkOrder :id="orderSummaryData.largestOrderInfo.id" :name="orderSummaryData.largestOrderInfo.sequenceNumber"></LinkOrder> for {{orderSummaryData.largestOrderInfo.contact.name}} on {{formatDate(orderSummaryData.largestOrderInfo.dateOrderCreated)}}</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
      </div>
      <div class="col-10">
        <ChartBar
          title="Yearly Order Value"
          :series="orderYearlyValueData.series"
          :xAxis="orderYearlyValueData.xAxis"
          :yAxis="orderYearlyValueData.yAxis"
          :legend="orderYearlyValueData.legend"
          :options="orderYearlyValueData.options"
          :popupEnabled="true"
          :exportEnabled="true"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import Utils from '@/utils/utils'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ChartBar from '@/components/charts/chartBar'

export default {
  name: 'OrderYearlyChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      orderSummaryData: {
        orderTotalValue: 0,
        orderTotalCount: 0,
        averageOrderValue: 0,
        largestOrderInfo: null
      },
      orderYearlyValueData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultOrderValueFilter: {    
        startDate: moment().local().subtract(7, 'years').startOf('year').toDate().toISOString(),
        endDate: moment().local().endOf('year').toDate().toISOString()
      },
      editingOrderValueFilter: {},
      appliedOrderValueFilter: {},
    }
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    }
  },
  mounted() {
    this.editingOrderValueFilter = _.clone(this.defaultOrderValueFilter)
    this.appliedOrderValueFilter = _.clone(this.defaultOrderValueFilter)

    this.requestChartData()

    this.subscribeEndpoint('/orders', () => {
      this.requestChartData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/orders')
  },
  watch: {
    contactId: function() {
      this.requestChartData()
    },
    filter: function() {
      this.requestChartData()
    }
  },
  methods: {
    applyOrderValueFilter() {
      this.appliedOrderValueFilter = _.clone(this.editingOrderValueFilter)

      this.requestChartData()
    },
    resetOrderValueFilter() {
      this.editingOrderValueFilter = _.clone(this.defaultOrderValueFilter)
      this.appliedOrderValueFilter = _.clone(this.defaultOrderValueFilter)

      this.requestChartData()
    },
    requestChartData() {
      var orderValueFilter = {
        type: 'yearly',
        startDate: this.appliedOrderValueFilter.startDate,
        endDate: this.appliedOrderValueFilter.endDate,
        contactId: this.contactId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null
      }
      let widgetComp = this

      Promise.all([
        this.$apiService.analyticsService.getOrdersValueChartData(orderValueFilter),
        this.$apiService.analyticsService.getOrdersValueSummary(orderValueFilter)
      ])
      .then(results => {
        let chartData = results[0].data
        let orderSummaryData = results[1].data

        this.orderYearlyValueData.legend = {
          data: ['Order Value', 'Order Count']
        }
        this.orderYearlyValueData.xAxis = {
          data: chartData.map(chartEntry => moment(chartEntry.date).format('YYYY'))
        }
        this.orderYearlyValueData.yAxis = [
          {
            type: 'value',
            axisLabel: {
              formatter: (value) => {
                return this.formatCurrencySuffix(value, 1);
              }
            }
          },
          { type: 'value', alignTicks: true }
        ]
        this.orderYearlyValueData.series = []
        this.orderYearlyValueData.series.push({
          type: 'bar',
          name: 'Order Value',
          data: chartData.map(chartEntry => chartEntry.totalOrderValue),
          tooltip: {
            valueFormatter(value) {
              return widgetComp.formatCurrency(value);
            }
          }
        })

        this.orderYearlyValueData.series.push({
          type: 'line',
          name: 'Order Count',
          yAxisIndex: 1,
          data: chartData.map(chartEntry => chartEntry.totalOrderCount),
        })

        // Summary info
        this.orderSummaryData.averageOrderValue = 0
        if(chartData.length > 0) {
          this.orderSummaryData.averageOrderValue = chartData.reduce((curValue, orderInfo) => curValue + orderInfo.totalOrderValue, 0) / chartData.length
        }

        this.orderSummaryData.orderTotalValue = orderSummaryData.orderTotalValue
        this.orderSummaryData.orderTotalCount = orderSummaryData.orderTotalCount
        this.orderSummaryData.largestOrderInfo = orderSummaryData.largestOrder
      })
    },
  }
}
</script>